import { useEffect, useMemo } from 'react';

import { Button, ErrorMessage } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import * as yup from 'yup';

import { createLinkBankAccountSchema } from './formValidation/linkBankAccountSchema';
import { useFormReduxBridge } from '../../../../customHooks/useFormReduxBridge';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import {
    REGISTER_BANK_ACCOUNT,
    REGISTER_DE_BANK_ACCOUNT
} from '../AddPaymentMethodModal.actions';
import styles from './LinkBankAccount.module.css';
import type { LinkBankAccountFormValues } from './LinkBankAccount.types';
import LinkDEBankAccountForm from './LinkDEBankAccount';
import { LinkUKBankAccount } from './LinkUKBankAccount';

const FORM_NAME = 'linkBankAccount';

// Not derived from `GetPaymentMethodResponse` (the usual source of truth) —
// that type is snake_case, but the saga reads camelCase and serialises itself.
type TRegisterBankAccountPayload = Pick<
    LinkBankAccountFormValues,
    'accountNumber' | 'sortCode' | 'iban'
>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addBankAccount: (bankAccountDetails: TRegisterBankAccountPayload) =>
        dispatch({
            type:
                getCountry() === 'DE'
                    ? REGISTER_DE_BANK_ACCOUNT
                    : REGISTER_BANK_ACCOUNT,
            ...bankAccountDetails
        })
});

const mapStateToProps = ({
    addPaymentMethodModal,
    api
}: {
    addPaymentMethodModal: RootState['addPaymentMethodModal'];
    api: RootState['api'];
}): {
    linkingBankAccount: boolean;
    registerBankAccountStatus: string | null;
    initialIban: string;
} => ({
    linkingBankAccount: addPaymentMethodModal.linkingBankAccount,
    registerBankAccountStatus: addPaymentMethodModal.registerBankAccountStatus,
    initialIban:
        _.find(api?.payment_method?.data, {
            payment_method_type: 'bank_account'
        })?.bank_details?.iban ?? ''
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const LinkBankAccountForm = ({
    initialIban,
    linkingBankAccount,
    registerBankAccountStatus,
    addBankAccount
}: PropsFromRedux) => {
    const intl = useIntl();
    const country = getCountry();

    const schema = useMemo(
        () => createLinkBankAccountSchema(intl, country),
        [intl, country]
    );

    const initialValues = useMemo<LinkBankAccountFormValues>(
        () => ({
            accountNumber: '',
            sortCode: '',
            iban: initialIban,
            directDebitMandate: false
        }),
        [initialIban]
    );

    const methods = useForm<LinkBankAccountFormValues>({
        defaultValues: initialValues
    });

    const { watch, getValues, setError, clearErrors, reset } = methods;

    // Mirrors the previous redux-form `enableReinitialize: true` behaviour:
    // when the payment_method API resolves and the IBAN populates, sync
    // RHF state with it.
    // Caveat: if `initialIban` flips mid-session (e.g. payment_method API
    // churn) any in-progress user input will be wiped. Same gotcha as the
    // legacy `enableReinitialize: true` — kept intentionally for parity.
    useEffect(() => {
        reset(initialValues);
    }, [reset, initialValues]);

    useFormReduxBridge<LinkBankAccountFormValues>(FORM_NAME, watch);

    const handleSave = () => {
        const values = getValues();
        clearErrors();
        try {
            schema.validateSync(values, { abortEarly: false });
            addBankAccount(
                _.pick(values, ['accountNumber', 'sortCode', 'iban'])
            );
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                for (const inner of err.inner) {
                    if (inner.path) {
                        setError(
                            inner.path as FieldPath<LinkBankAccountFormValues>,
                            {
                                type: inner.type ?? 'validate',
                                message: inner.message
                            }
                        );
                    }
                }
            }
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                data-testid="link-bank-account-form"
                className={styles.LinkBankAccountForm}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
            >
                {country === 'DE' ? (
                    <LinkDEBankAccountForm />
                ) : (
                    <LinkUKBankAccount />
                )}
                <div className="mt-xl flex flex-col items-start gap-m">
                    <Button
                        type="button"
                        data-testid="link-bank-account-button"
                        loading={linkingBankAccount}
                        onClick={handleSave}
                    >
                        <FormattedMessage
                            id="_.modal.link_bank_account.save"
                            defaultMessage="Link this account"
                        />
                    </Button>
                    {registerBankAccountStatus === 'error' ? (
                        <ErrorMessage>
                            <FormattedMessage
                                id="_.confirmation_button.error"
                                defaultMessage="Error"
                            />
                        </ErrorMessage>
                    ) : null}
                </div>
            </form>
        </FormProvider>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LinkBankAccountForm);
